import { Directive, ElementRef, AfterViewInit, HostListener } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { timeout } from 'rxjs/operators';

import { APP_NAME_HEADER } from '@finder/shared/constants/app.constants';
import { WindowRef } from '@finder/shared/services/window-ref/window-ref.service';
import { toParams }  from '@finder/shared/utils/url/toParams';
import { stripLocaleFromUrl } from '@finder/shared/utils/url/stripLocaleFromUrl';

@Directive({
    selector: '[finderRouterLink]',
})
export class RouterLinkDirective implements AfterViewInit {

    url: string;

    constructor(
        private http: HttpClient,
        private windowRef: WindowRef,
        private elementRef: ElementRef,
        private router: Router
    ) { }

    @HostListener('click', ['$event'])
    handleClick($event = null) {

        // Prevent link from reloading the page.
        if ($event) {
            $event.preventDefault();
        }

        this.http.head(this.url, { observe: 'response', headers: { accept: 'text/html' } })
            .pipe(timeout(1000))
            .subscribe({
                next: (response: any) => {
                    this.checkAppNameHeader(response);
                },
                error: () => {
                    // If the HEAD request took more than 1 second
                    // or if there was an unexpected error just go to the url.
                    this.goToExternalUrl();
                }
            });
    }

    ngAfterViewInit() {
        this.url = this.elementRef.nativeElement.getAttribute('href');
    }

    /**
     * @name goToExternalLink
     * @description Navigates to the url by changing the window location.
     */
    private goToExternalUrl() {
        this.windowRef.nativeWindow.location.href = this.url;
    }

    /**
     * @name checkAppNameHeader
     * @description Checks the App Name header to determine
     * if the url should be handled by Nemo or not.
     * @param response HEAD response
     */
    private checkAppNameHeader(response) {
        const appName = response.headers.get(APP_NAME_HEADER.name);

        // this.url is the clicked-on URL
        const clickedUrl = new URL(this.url, this.windowRef.nativeWindow.location.origin);

        // response.url is the end destination after clicking this.url; it is the result *after* the 301.
        const responseUrl = new URL(response.url, this.windowRef.nativeWindow.location.origin);

        // if the clicked URL matches the response URL--excluding any locales prepended by akamai--
        // we assume there is no redirect in place; we can handle the link in-SPA
        const handleInSpa = (clickedUrl.pathname === stripLocaleFromUrl(responseUrl.pathname));

        // Handle link using Angular Router.
        if (appName && appName === APP_NAME_HEADER.value && handleInSpa) {
            // Since the matched route won't change
            // we need to tell the router not to reuse the route
            // so the component gets reloaded.
            this.router.routeReuseStrategy.shouldReuseRoute = () => false;
            this.router.onSameUrlNavigation = 'reload';

            // https://myjira.disney.com/browse/GIT-31034
            // We need to check if the URL has a hash/search in it and if it does we need to
            // call `navigate` differently
            try {
                const jsUrl = new URL(this.url, this.windowRef.nativeWindow.location.origin);
                if (jsUrl.hash || jsUrl.search) {
                    const extras: any = {};
                    if (jsUrl.hash) {
                        extras.fragment = jsUrl.hash.replace(/^#/, '');
                    }
                    if (jsUrl.search) {
                        extras.queryParams = toParams(jsUrl.search);
                    }
                    this.router.navigate([jsUrl.pathname], extras);
                } else {
                    this.router.navigate([jsUrl.pathname]);
                }
            } catch (e) /* istanbul ignore next */ {
                // The `new URL` call can throw if the URL and base are not properly
                // parseable values, so just to be safe we will route the old fashioned
                // way if there is an issue.
                this.goToExternalUrl();
            }
        } else {
            this.goToExternalUrl();
        }
    }
}
